import { Box, Text } from '@chakra-ui/react';
import { useState } from 'react';

import {
  MarketplaceSubEvent,
  OfferActionSurface,
} from '../../../../../../../services/analytics/marketplaceEvents';
import { usePostLeadByExternalIdOffers } from '../../../../../../../services/marketplace/generated/kubb/hooks/usePostLeadByExternalIdOffers';
import { formatMoney } from '../../../../../../../utils/helpers';
import { useOfferAnalytics } from '../OfferAnalyticsContext';
import { useOfferActionFeedback } from '../useOfferActionFeedback';
import { MarketplaceOffer, OfferCardView, getOfferErrorMessage, isOfferStale } from '../utils';
import CounterNoteField, { isCounterNoteValid } from './CounterNoteField';
import MoneyInput from './MoneyInput';
import OfferActionModal from './OfferActionModal';
import OfferModalActions from './OfferModalActions';
import StaleOfferBanner from './StaleOfferBanner';

type CounterModalProps = {
  card: OfferCardView;
  surface: OfferActionSurface;
  liveOffers: MarketplaceOffer[];
  externalId: string;
  onClose: () => void;
  onSuccess: () => void;
};

const CounterModal = ({
  card,
  surface,
  liveOffers,
  externalId,
  onClose,
  onSuccess,
}: CounterModalProps) => {
  const [rawDigits, setRawDigits] = useState('');
  const [note, setNote] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutate, isPending } = usePostLeadByExternalIdOffers();
  const { refetchOffers } = useOfferActionFeedback(externalId);
  const { trackOfferEvent } = useOfferAnalytics();
  const isStale = isOfferStale(card.offer, liveOffers);

  const parsedAmount = rawDigits === '' ? 0 : Number(rawDigits);
  const isSameAsOffer = parsedAmount > 0 && parsedAmount === card.amount;
  const isValid = parsedAmount > 0 && !isSameAsOffer && isCounterNoteValid(note);

  const handleSubmit = () => {
    mutate(
      {
        externalId,
        data: {
          action: 'driver_counter',
          amount: parsedAmount,
          parentOfferId: card.offer.id,
          notes: note.trim(),
        },
      },
      {
        onSuccess: () => {
          trackOfferEvent(MarketplaceSubEvent.OfferCounterSubmitted, {
            offerId: card.offer.id,
            dealerLabel: card.dealerLabel,
            dealerAmount: card.amount,
            driverAmount: parsedAmount,
            surface,
          });
          refetchOffers();
          onSuccess();
          onClose();
        },
        onError: (error) => {
          trackOfferEvent(MarketplaceSubEvent.OfferActionFailed, {
            offerId: card.offer.id,
            dealerLabel: card.dealerLabel,
            dealerAmount: card.amount,
            actionType: 'counter',
            surface,
          });
          setErrorMessage(getOfferErrorMessage(error) ?? 'Something went wrong, please try again.');
        },
      },
    );
  };

  const handleEnter = () => {
    if (isValid && !isPending) handleSubmit();
  };

  const hint =
    errorMessage ??
    (isSameAsOffer
      ? `Same as the current offer. Close this and tap Accept on the card instead.`
      : null);

  return (
    <OfferActionModal
      isOpen
      onClose={isPending ? () => undefined : onClose}
      title={`Counter Buyer ${card.dealerLabel}'s offer`}
    >
      <Box px={{ base: '24px', md: '32px' }} pb={{ base: '24px', md: '32px' }} pt="16px">
        {isStale && <StaleOfferBanner />}

        <Text fontSize="13px" color="gray.500" mb="20px">
          Buyer {card.dealerLabel} offered <strong>{formatMoney(card.amount)}</strong>. Enter your
          counter.
        </Text>

        <Text fontSize="13px" fontWeight="600" color="leaseEndBlue" mb="8px">
          What's your counter offer?
        </Text>

        <MoneyInput
          rawDigits={rawDigits}
          onDigitsChange={(digits) => {
            setRawDigits(digits);
            setErrorMessage(null);
          }}
          isWarn={isSameAsOffer || errorMessage != null}
          onEnter={handleEnter}
        />

        {hint && (
          <Text fontSize="12px" color="orange.500" mb="6px" fontWeight="500">
            {hint}
          </Text>
        )}

        <Text fontSize="11.5px" color="gray.400" lineHeight="1.5">
          Single number, no decimals needed.
        </Text>

        <CounterNoteField note={note} onNoteChange={setNote} />

        <Text mt="16px" fontSize="11.5px" color="gray.400" lineHeight="1.5" mb="8px">
          Any buyer can accept the counter you've sent them at any time. The moment one is accepted,
          your other negotiations end automatically.
        </Text>

        <OfferModalActions
          mt="20px"
          onCancel={onClose}
          onPrimary={handleSubmit}
          primaryLabel="Send Counter"
          isPending={isPending}
          isPrimaryDisabled={!isValid || isStale}
        />
      </Box>
    </OfferActionModal>
  );
};

export default CounterModal;
